import React, { useCallback, useEffect, useMemo, useState } from 'react';
import update from 'immutability-helper';
import { useFormik } from 'formik';
import { NotFound } from 'src/components/common';
import styles from './knowledge-link-editor.module.scss';
import { KnowledgeWrapperComponent } from '../knowledge-row';
import { Lang } from 'shared/constants';
import { useSearch } from 'shared/hooks/search-v2.hooks';
import { getKnowledgeItemHits } from 'shared/utils/search-v2-mapper';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { Modal, SearchBox } from 'src/components/design-system';
import * as Yup from 'yup';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { Card } from 'antd';

const formatError = (e, data, lang) => {
  const id = e.split(' ')[1].replace('(', '').replace(')', '');
  const item = data.find(item => item.id === id);

  return lang.ALREADY_BELONGS_ERROR.replace('{title}', item.title);
};

const KNOWLEDGE_LINK_SCHEMA = Yup.object().shape({
  linkOwnerIds: Yup.array()
    .of(Yup.string())
    .min(1, 'Please assign at least one owner')
    .required('Required!'),
});

const KnowledgeLinkEditorComponent = React.memo(function KnowledgeLinkEditorComponent({
  open = true,
  categoryId,
  onFinish,
  createKnowledgeLink,
  lang = Lang.KNOWLEDGE_LINK_EDITOR,
  parentOwnerIds = [],
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState('');
  const { data: searchResults, isFetching, refetch } = useSearch(term);
  const knowledgeItems = useMemo(() => getKnowledgeItemHits(searchResults), [searchResults]);

  useEffect(() => {
    setError(null);
  }, [selectedItems]);

  const onSubmit = useCallback(
    async data => {
      setIsLoading(true);

      try {
        await createKnowledgeLink({
          ...data,
          linkOwnerIds: data.linkOwnerIds,
          categoryId,
          items: selectedItems,
        });
        onFinish();
        setError(null);
      } catch (e) {
        setError(formatError(e.message, selectedItems, lang));
      } finally {
        setIsLoading(false);
      }
    },
    [onFinish, createKnowledgeLink, categoryId, selectedItems, lang],
  );

  // Searching the same term again asks the server again.
  const onSearch = useCallback(
    value => {
      const next = value.trim();
      if (next && next === term) refetch();
      else setTerm(next);
    },
    [term, refetch],
  );

  const selectItem = useCallback(
    item => {
      setSelectedItems(
        update(selectedItems, {
          $push: [item],
        }),
      );
    },
    [selectedItems],
  );

  const removeItem = useCallback(
    item => {
      const index = selectedItems.findIndex(({ id }) => id === item.id);
      setSelectedItems(
        update(selectedItems, {
          $splice: [[index, 1]],
        }),
      );
    },
    [selectedItems],
  );

  const { handleSubmit, setFieldValue, values, errors, isValid } = useFormik({
    enableReinitialize: true,
    validationSchema: KNOWLEDGE_LINK_SCHEMA,
    initialValues: {
      linkOwnerIds: [],
    },
    onSubmit,
  });

  const dismiss = useCallback(() => {
    setSelectedItems([]);
    setError(null);
    setTerm('');
    onFinish();
  }, [onFinish]);

  return (
    <Modal
      title={lang.CREATE_TITLE}
      open={open}
      onCancel={dismiss}
      afterOpenChange={isOpen => {
        if (!isOpen) {
          setSelectedItems([]);
          setError(null);
          setTerm('');
        }
      }}
      onSubmit={handleSubmit}
      cancelText={lang.DISMISS}
      submitText={lang.SUBMIT}
      loading={isLoading}
      submitDisabled={!isValid || error || selectedItems.length === 0 || isLoading}
      width="75%"
      styles={{
        body: {
          maxHeight: '75vh',
        },
      }}>
      <form onSubmit={handleSubmit}>
        <div className={styles.searchAndOwner}>
          <div className={styles.owner}>
            <label>{lang.OWNER}</label>
            <MultipleOwnersSelector
              value={values.linkOwnerIds}
              error={errors.linkOwnerIds}
              placeholder={lang.OWNER_PLACEHOLDER}
              name="linkOwnerIds"
              onChange={setFieldValue}
              borderRadius={4}
              parentOwnerIds={parentOwnerIds}
            />
          </div>
          <label>{lang.SEARCH}</label>
          <SearchBox
            onSearch={onSearch}
            placeholder={lang.SEARCH}
            enterButton={lang.SEARCH_BUTTON}
            loading={isFetching}
          />
        </div>
        <div className={styles.container}>
          <div className={styles.column}>
            <Card title={lang.RESULTS}>
              {knowledgeItems.length > 0 && (
                <ListItemsNavigator orientation="vertical" className={styles.list}>
                  {knowledgeItems
                    .filter(({ item }) => !selectedItems.some(({ id }) => id === item.id))
                    .map(({ item }) => (
                      <KnowledgeWrapperComponent
                        key={`${item.id}-link`}
                        item={item}
                        selectItem={selectItem}
                        removeItem={removeItem}
                        search
                        editMode
                        adminMode
                        linkedItem
                        preventOpen
                        allowMove
                      />
                    ))}
                </ListItemsNavigator>
              )}
            </Card>
          </div>
          <div className={styles.column}>
            <Card title={lang.SELECTED}>
              {selectedItems.length > 0 ? (
                <ListItemsNavigator orientation="vertical" className={styles.list}>
                  {selectedItems.map(item => (
                    <KnowledgeWrapperComponent
                      key={`${item.id}-link`}
                      item={item}
                      selectItem={selectItem}
                      removeItem={removeItem}
                      selected
                      editMode
                      adminMode
                      linkedItem
                      preventOpen
                      allowMove
                    />
                  ))}
                </ListItemsNavigator>
              ) : (
                <NotFound className={styles.notFound} />
              )}
              {error && <span>{error}</span>}
            </Card>
          </div>
        </div>
      </form>
    </Modal>
  );
});

export { KnowledgeLinkEditorComponent };
